import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { customAlphabet } from 'nanoid';
import { CartItem, MenuItem, CartItemAddon, CartItemRemoval } from '../types';

const createLineId = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 10);

interface CartState {
  items: CartItem[];
  addItem: (item: MenuItem & { selectedAddons?: CartItemAddon[], selectedRemovals?: CartItemRemoval[]; groupId?: string; groupName?: string; variantItemId?: string; variantName?: string }) => void;
  removeItem: (lineId: string) => void;
  updateQuantity: (lineId: string, quantity: number) => void;
  clearCart: () => void;
  total: number;
}

const calculateItemTotal = (item: CartItem): number => {
  const addonsTotal = Array.isArray(item.selectedAddons)
    ? item.selectedAddons
        .filter(addon => addon && addon.selected && Number.isFinite(addon.price) && addon.price >= 0)
        .reduce((sum, addon) => sum + addon.price, 0)
    : 0;
  return (item.price + addonsTotal) * item.quantity;
};

export const useCartStore = create<CartState>()(persist((set, get) => ({
  items: [],
  total: 0,
  addItem: (item) => {
    const items = get().items;
    const existingItemIndex = items.findIndex((i) => {
      // Check if item exists with same addons and removals configuration
      const existingVariantId = i.variantItemId || i.id;
      const nextVariantId = item.variantItemId || item.id;
      if (existingVariantId !== nextVariantId) return false;
      if ((i.groupId || '') !== (item.groupId || '')) return false;

      const existingAddons = i.selectedAddons || [];
      const newAddons = item.selectedAddons || [];

      if (existingAddons.length !== newAddons.length) return false;

      const addonsMatch = existingAddons.every(addon => {
        const matchingAddon = newAddons.find(a => a.id === addon.id);
        return matchingAddon && matchingAddon.selected === addon.selected;
      });

      if (!addonsMatch) return false;

      const existingRemovals = i.selectedRemovals || [];
      const newRemovals = item.selectedRemovals || [];

      if (existingRemovals.length !== newRemovals.length) return false;

      return existingRemovals.every(removal => {
        const matchingRemoval = newRemovals.find(r => r.id === removal.id);
        return matchingRemoval && matchingRemoval.selected === removal.selected;
      });
    });

    if (existingItemIndex !== -1) {
      // Update quantity of existing item
      const updatedItems = [...items];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + 1
      };
      set({ items: updatedItems });
    } else {
      // Add new item
      set({
        items: [...items, {
          ...item,
          lineId: createLineId(),
          quantity: 1,
          selectedAddons: item.selectedAddons || [],
          selectedRemovals: item.selectedRemovals || []
        }]
      });
    }

    set(state => ({
      total: state.items.reduce((sum, item) => sum + calculateItemTotal(item), 0)
    }));
  },
  removeItem: (lineId) => {
    set((state) => {
      const newItems = state.items.filter((i) => i.lineId !== lineId);
      return {
        items: newItems,
        total: newItems.reduce((sum, item) => sum + calculateItemTotal(item), 0)
      };
    });
  },
  updateQuantity: (lineId, quantity) => {
    set((state) => {
      const newItems = state.items.map((i) =>
        i.lineId === lineId ? { ...i, quantity } : i
      );
      return {
        items: newItems,
        total: newItems.reduce((sum, item) => sum + calculateItemTotal(item), 0)
      };
    });
  },
  clearCart: () => set({ items: [], total: 0 }),
}), {
  name: `forgedos-cart-${import.meta.env.VITE_RESTAURANT_ID || 'local'}`,
  version: 1,
  partialize: (state) => ({ items: state.items }),
  merge: (saved, current) => {
    const value = saved as { items?: CartItem[] } | undefined;
    const items = Array.isArray(value?.items) ? value.items.filter((item) => item && typeof item.id === 'string' && typeof item.lineId === 'string'
      && Number.isFinite(item.price) && item.price >= 0 && Number.isInteger(item.quantity) && item.quantity > 0 && item.quantity <= 100).slice(0,100) : [];
    return { ...current, items, total: items.reduce((sum, item) => sum + calculateItemTotal(item), 0) };
  },
}));
